import { cn } from '@amore/ui/cn'
import { ChevronDown } from 'lucide-react'
import { type CSSProperties, type ReactNode, useState } from 'react'

export type Column<T> = {
  key: string
  header: string
  /** A CSS grid track: `minmax(0,1fr)` for the primary text, a fixed `9rem` for the rest. */
  width: string
  /** Money and counts are `end`; everything else `start`. */
  align?: 'start' | 'end' | 'center'
  cell: (row: T) => ReactNode
  /** The row's title: first on mobile, never hidden, and the row's click target. Exactly one column. */
  primary?: boolean
  /** On mobile, hide the cell or show it as a labelled line (the default). */
  mobile?: 'hide' | 'line'
  /** Money/number cells use tabular figures. */
  numeric?: boolean
  /** The cell holds its own control (e.g. a status select), so it sits above the row's click target. */
  interactive?: boolean
}

export type TableGroup<T> = {
  id: string
  label: ReactNode
  /** Small text after the label, e.g. "3 položky". */
  meta?: ReactNode
  rows: T[]
  /** Values aligned under the given columns, e.g. a category subtotal under the money column. */
  summary?: Record<string, ReactNode>
  /** The header row toggles the group's rows, with a chevron at the row's end. */
  collapsible?: boolean
  /** Starts collapsed, e.g. "Hotové". */
  defaultCollapsed?: boolean
  /** `danger` tints the header red for a group that needs action now, e.g. "Po splatnosti". */
  tone?: 'danger'
}

type Props<T> = {
  columns: Column<T>[]
  getRowId: (row: T) => string
  /** Opens the record's detail. The whole row is the click target. */
  onRowClick?: (row: T) => void
  /** The right-hand actions cell: one icon button or a "⋯" menu. */
  rowActions?: (row: T) => ReactNode
  /** Dims a row (cancelled, done). */
  rowMuted?: (row: T) => boolean
  /** `danger` tints a row red with a bar on its left edge: it needs action now (overdue). */
  rowTone?: (row: T) => 'danger' | undefined
  /** Shown as one row when there's nothing to list. */
  empty?: ReactNode
  'aria-label': string
} & ({ rows: T[]; groups?: never } | { groups: TableGroup<T>[]; rows?: never })

const alignClass = {
  start: 'md:justify-start md:text-left',
  end: 'md:justify-end md:text-right',
  center: 'md:justify-center md:text-center',
}
const gridRow = 'md:grid md:grid-cols-(--table-columns) md:items-center md:gap-4'
const dangerEdge = 'shadow-[inset_3px_0_0_var(--color-danger-foreground)]'

/**
 * Lists of records with real column culture: declared widths, a header aligned
 * with the cells, money right-aligned in tabular figures, a fixed status column,
 * actions on the far right. Rows collapse to stacked cards below `md`.
 */
export function DataTable<T>(props: Props<T>) {
  const { columns, getRowId, onRowClick, rowActions, rowMuted, rowTone, empty } = props
  const groups: TableGroup<T>[] = props.groups ?? [{ id: 'all', label: null, rows: props.rows }]
  // The actions track also holds a collapsible group's chevron.
  const actionsTrack = Boolean(rowActions) || groups.some((group) => group.collapsible)
  const template = `${columns.map((column) => column.width).join(' ')}${actionsTrack ? ' 3rem' : ''}`
  const style = { '--table-columns': template } as CSSProperties
  const total = groups.reduce((count, group) => count + group.rows.length, 0)
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(groups.map((group) => [group.id, Boolean(group.defaultCollapsed)])),
  )
  const isCollapsed = (group: TableGroup<T>) =>
    Boolean(group.collapsible && (collapsed[group.id] ?? group.defaultCollapsed))

  return (
    <div className="overflow-hidden rounded-panel border border-border bg-card" style={style}>
      <table aria-label={props['aria-label']} className="block w-full text-sm">
        <thead className="hidden md:block">
          <tr
            className={cn(
              gridRow,
              'border-b border-border bg-muted/50 px-5 py-2.5 text-xs font-semibold text-muted-foreground',
            )}
          >
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                className={cn('flex font-semibold', alignClass[column.align ?? 'start'])}
              >
                {column.header}
              </th>
            ))}
            {actionsTrack ? (
              <th scope="col">
                <span className="sr-only">Akce</span>
              </th>
            ) : null}
          </tr>
        </thead>

        {total === 0 && empty ? (
          <tbody className="block">
            <tr className="block px-5 py-10 text-center text-muted-foreground">
              <td className="block">{empty}</td>
            </tr>
          </tbody>
        ) : null}

        {groups.map((group) =>
          group.rows.length === 0 && group.label === null ? null : (
            <tbody key={group.id} className="block">
              {group.label !== null ? (
                <GroupRow
                  columns={columns}
                  group={group}
                  hasActions={actionsTrack}
                  collapsed={isCollapsed(group)}
                  onToggle={() =>
                    setCollapsed((current) => ({ ...current, [group.id]: !isCollapsed(group) }))
                  }
                />
              ) : null}
              {(isCollapsed(group) ? [] : group.rows).map((row) => (
                <tr
                  key={getRowId(row)}
                  className={cn(
                    gridRow,
                    'relative flex flex-col gap-1.5 border-b border-border px-5 py-3.5 last:border-b-0',
                    onRowClick && 'transition-colors hover:bg-muted/40',
                    rowMuted?.(row) && 'text-muted-foreground',
                    rowTone?.(row) === 'danger' && [dangerEdge, 'bg-danger/35 hover:bg-danger/60'],
                  )}
                >
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={cn(
                        'min-w-0 md:flex md:items-center',
                        alignClass[column.align ?? 'start'],
                        column.numeric && 'tabular-nums',
                        column.interactive && 'relative z-10',
                        column.primary && 'order-first pr-12 font-medium md:order-none md:pr-0',
                        !column.primary && column.mobile === 'hide' && 'hidden md:flex',
                        !column.primary &&
                          column.mobile !== 'hide' &&
                          'flex items-baseline justify-between gap-3 text-xs md:text-sm',
                      )}
                    >
                      {!column.primary && column.mobile !== 'hide' ? (
                        <span className="text-muted-foreground md:hidden">{column.header}</span>
                      ) : null}
                      {column.primary && onRowClick ? (
                        <button
                          type="button"
                          onClick={() => onRowClick(row)}
                          className="min-w-0 text-left after:absolute after:inset-0 after:content-[''] focus-visible:outline-none focus-visible:after:outline-2 focus-visible:after:-outline-offset-2 focus-visible:after:outline-ring"
                        >
                          {column.cell(row)}
                        </button>
                      ) : (
                        column.cell(row)
                      )}
                    </td>
                  ))}
                  {rowActions ? (
                    <td className="absolute top-2 right-2 z-10 flex justify-end md:static">
                      {rowActions(row)}
                    </td>
                  ) : actionsTrack ? (
                    <td className="hidden md:block" />
                  ) : null}
                </tr>
              ))}
            </tbody>
          ),
        )}
      </table>
    </div>
  )
}

/**
 * A group header: the label spans the leading columns up to the first column
 * with a summary value (or the whole row), so a narrow first column (e.g. a
 * priority icon) never squeezes the label. A collapsible group is one big
 * toggle with its chevron at the row's end, in the actions column.
 */
function GroupRow<T>({
  columns,
  group,
  hasActions,
  collapsed,
  onToggle,
}: {
  columns: Column<T>[]
  group: TableGroup<T>
  hasActions: boolean
  collapsed: boolean
  onToggle: () => void
}) {
  const firstSummary = columns.findIndex(
    (column, index) => index > 0 && group.summary?.[column.key] !== undefined,
  )
  const span = firstSummary === -1 ? columns.length : firstSummary
  const chevron = group.collapsible ? (
    <ChevronDown
      aria-hidden="true"
      className={cn('size-4 text-muted-foreground transition-transform', collapsed && '-rotate-90')}
    />
  ) : null
  return (
    <tr
      className={cn(
        gridRow,
        'relative flex items-center justify-between gap-4 border-b border-border px-5 py-2.5',
        group.tone === 'danger'
          ? [dangerEdge, 'bg-danger text-danger-foreground']
          : 'bg-panel-accent/60',
        group.collapsible && 'transition-colors hover:bg-panel-accent',
      )}
    >
      <th
        scope="rowgroup"
        style={{ gridColumn: `1 / span ${span}` }}
        className="flex min-w-0 items-baseline gap-2 text-left"
      >
        {group.collapsible ? (
          <button
            type="button"
            aria-expanded={!collapsed}
            onClick={onToggle}
            className="flex min-w-0 items-baseline gap-2 text-left after:absolute after:inset-0 after:content-[''] focus-visible:outline-none focus-visible:after:outline-2 focus-visible:after:-outline-offset-2 focus-visible:after:outline-ring"
          >
            <span className="font-semibold">{group.label}</span>
            {group.meta ? (
              <span
                className={cn(
                  'text-xs font-normal',
                  group.tone === 'danger' ? 'text-danger-foreground/80' : 'text-muted-foreground',
                )}
              >
                {group.meta}
              </span>
            ) : null}
          </button>
        ) : (
          <>
            <span className="font-semibold">{group.label}</span>
            {group.meta ? (
              <span
                className={cn(
                  'text-xs font-normal',
                  group.tone === 'danger' ? 'text-danger-foreground/80' : 'text-muted-foreground',
                )}
              >
                {group.meta}
              </span>
            ) : null}
          </>
        )}
      </th>
      {firstSummary === -1
        ? null
        : columns.slice(span).map((column) => (
            <td
              key={column.key}
              className={cn(
                'font-semibold tabular-nums',
                group.summary?.[column.key] ? 'flex' : 'hidden md:flex',
                alignClass[column.align ?? 'start'],
              )}
            >
              {group.summary?.[column.key]}
            </td>
          ))}
      {hasActions ? <td className="flex justify-center">{chevron}</td> : null}
    </tr>
  )
}
